import { nowIsoAndUnix } from "@@/shared/date-time/iso-and-unix";
import type {
    ReservationCheckInStatus} from "@@/shared/statuses/reservation-check-ins/reservation-check-in-status";
import {
    reservationCheckInStatusFactory,
} from "@@/shared/statuses/reservation-check-ins/reservation-check-in-status";
import type { StatusEvent } from "@@/shared/statuses/status-event";
import type { StatusId } from "@@/shared/statuses/status-id";
import type {
    StatusReferences} from "@@/shared/statuses/status-references";
import {
    isStatusReferenceMatch,
} from "@@/shared/statuses/status-references";
import { systemUser } from "@@/shared/users/system-user";
import { sortBy } from "@@/utilities/sort";
import { produce } from "immer";
import type { SetRequired } from "type-fest";

const reservationStatusReferencesToCheckInId = (
    references: StatusReferences,
): StatusId => {
    return `reservation_status__check_in__${Object.entries(references)
        .sort(sortBy(([key]) => key))
        .map(([_, value]) => value)
        .join("_")}` as StatusId;
};

function reservationCheckInStatusReducer(params: {
    events: StatusEvent[];
    statusFor: SetRequired<
        StatusReferences,
        "resourceReservationId" | "resourceId"
    >;
    /**
     * Provides the current (not the time of the event)
     * resource checkInEnabled boolean value in the projection
     */
    checkInEnabledForResource: boolean;
    initialStatus?: ReservationCheckInStatus;
}): ReservationCheckInStatus {
    const { events, statusFor, initialStatus, checkInEnabledForResource } =
        params;
    const eventsFiltered = events.filter(event =>
        isStatusReferenceMatch(statusFor, event.references),
    );

    // Set initial status
    // Set it to the provided status or if no initial status is provided,
    // since we know there are events, create an initial status
    const firstEvent = events[0];
    const _initialStatus = initialStatus
        ? {
              ...initialStatus,
              checkInEnabled: checkInEnabledForResource,
          }
        : reservationCheckInStatusFactory({
              _id: reservationStatusReferencesToCheckInId(statusFor),
              references: statusFor,
              status: "PENDING",
              setBy: systemUser,
              setAt: firstEvent?._createdAt ?? nowIsoAndUnix(),
              checkInEnabled: checkInEnabledForResource,
          });

    // If no events are provided, return the initial status
    if (!eventsFiltered.length)
        return _initialStatus as ReservationCheckInStatus;

    const projection = eventsFiltered
        .sort(sortBy(event => event._createdAt.iso))
        .reduce((status, event) => {
            switch (event.type) {
                case "CHECK_IN__CHECKED_IN":
                    return produce(status, draft => {
                        draft.setBy = event._createdBy;
                        draft.status = "CHECKED_IN";
                        draft.setAt = event._createdAt;
                        draft.message = event.payload?.message;
                    });
                case "CHECK_IN__CHECKED_OUT":
                    return produce(status, draft => {
                        draft.setBy = event._createdBy;
                        draft.status = "CHECKED_OUT";
                        draft.setAt = event._createdAt;
                        draft.message = event.payload?.message;
                    });
                case "CHECK_IN__INITIATED":
                case "CHECK_IN__PENDING":
                    return produce(status, draft => {
                        draft.setBy = event._createdBy;
                        draft.status = "PENDING";
                        draft.setAt = event._createdAt;
                        draft.message = event.payload?.message;
                    });
                default:
                    return status;
            }
        }, _initialStatus);

    return projection;
}
export { reservationCheckInStatusReducer };
